import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plug, X } from 'lucide-react';

// Announces Pearson in Claude (MCP connector) and Copilot. Shown on the first
// SHOW_TIMES app loads in this browser and stays until closed; never again once
// the user follows the set-up link.
const KEY = 'fm-announce-connectors-v1';
const SHOW_TIMES = 3;

// Decided once per page load, so the chat page re-mounting during the login check
// keeps showing it instead of counting again or dropping it.
let showThisLoad: boolean | null = null;
let closed = false;

function countKey(email: string) {
  return `${KEY}:${email.toLowerCase()}`;
}

export default function ConnectorsAnnouncement({ email }: { email?: string }) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!email) return;
    if (showThisLoad === null) {
      try {
        const count = Number(localStorage.getItem(countKey(email)) || 0);
        showThisLoad = count < SHOW_TIMES;
        if (showThisLoad) localStorage.setItem(countKey(email), String(count + 1));
      } catch {
        showThisLoad = false; // storage blocked (private mode etc.): skip it
      }
    }
    setVisible(showThisLoad && !closed);
  }, [email]);

  const close = (forever: boolean) => {
    closed = true;
    setVisible(false);
    if (forever && email) {
      try {
        localStorage.setItem(countKey(email), String(SHOW_TIMES));
      } catch {
        // ignore
      }
    }
  };

  if (!visible) return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        right: '20px',
        bottom: '20px',
        zIndex: 1500,
        width: '360px',
        maxWidth: 'calc(100vw - 32px)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderLeft: '3px solid var(--accent)',
        borderRadius: '12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
        padding: '14px 16px',
        display: 'flex',
        gap: '12px',
        color: 'var(--text)',
      }}
    >
      <div style={{
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        background: '#8b5cf618',
        color: 'var(--accent)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Plug size={16} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
          Pearson is now in Claude and Copilot
        </div>
        <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Ask the same questions from Claude or Microsoft 365 Copilot with the Pearson FA MCP connector.
        </div>
        <button
          type="button"
          onClick={() => {
            close(true);
            navigate('/glossary#claude-copilot');
          }}
          style={{
            marginTop: '8px',
            padding: 0,
            background: 'none',
            border: 'none',
            color: 'var(--accent)',
            fontSize: '12.5px',
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          How to set it up →
        </button>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => close(false)}
        style={{
          alignSelf: 'flex-start',
          background: 'none',
          border: 'none',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          padding: '2px',
          display: 'flex',
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
